import { supabase } from '@/lib/supabase';
import type { OrderStatus } from '@/types';
import type { SalesMetrics } from '@/store/seller';

// 판매자 대시보드 데이터
export interface SellerDashboardData {
  salesMetrics: SalesMetrics;
  reviewCount: number;
  businessStatus: boolean;
}

/**
 * 판매자 대시보드 데이터를 가져옵니다
 */
export async function getDashboardData(restaurantId: string): Promise<{
  success: boolean;
  data?: SellerDashboardData;
  error?: string;
}> {
  try {
    // 1. 당월 매출 및 판매 건수 조회 (완료된 주문만)
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
    const { data: salesData, error: salesError } = await supabase
      .from('orders')
      .select('total_amount, created_at')
      .eq('restaurant_id', restaurantId)
      .eq('status', 'COMPLETED')
      .gte('created_at', `${currentMonth}-01`)
      .lt('created_at', `${new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString()}`);

    if (salesError) {
      console.error('Sales data fetch error:', salesError);
      throw salesError;
    }

    const monthlyRevenue = salesData?.reduce((sum, order) => sum + order.total_amount, 0) || 0;
    const monthlySalesCount = salesData?.length || 0;

    // 2. 최근 6시간 내 리뷰 수 조회
    const sixHoursAgo = new Date();
    sixHoursAgo.setHours(sixHoursAgo.getHours() - 6);
    
    const { data: reviewsData, error: reviewsError } = await supabase
      .from('reviews')
      .select('id')
      .eq('restaurant_id', restaurantId)
      .gte('created_at', sixHoursAgo.toISOString());

    if (reviewsError) {
      console.error('Reviews data fetch error:', reviewsError);
      throw reviewsError;
    }

    const reviewCount = reviewsData?.length || 0;

    // 3. 영업 상태 조회
    const { data: restaurantData, error: restaurantError } = await supabase
      .from('restaurants')
      .select('is_open')
      .eq('id', restaurantId)
      .single();

    if (restaurantError) {
      console.error('Restaurant data fetch error:', restaurantError);
      throw restaurantError;
    }

    return {
      success: true,
      data: {
        salesMetrics: {
          monthlyRevenue,
          monthlySalesCount,
          currentMonth: `${new Date().getFullYear()}년 ${new Date().getMonth() + 1}월`
        },
        reviewCount,
        businessStatus: restaurantData.is_open
      }
    };
  } catch (error) {
    console.error('Dashboard data fetch error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '대시보드 데이터를 불러오는데 실패했습니다'
    };
  }
}

/**
 * 주문 카운트를 가져옵니다 (모든 상태별)
 */
export async function getOrderCounts(restaurantId: string): Promise<{
  success: boolean;
  data?: Record<OrderStatus, number>;
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('status, payment_status')
      .eq('restaurant_id', restaurantId);

    if (error) {
      console.error('Order counts fetch error:', error);
      throw error;
    }

    const counts: Record<OrderStatus, number> = {
      PENDING: 0,
      ACCEPTED: 0,
      READY: 0,
      COMPLETED: 0,
      CANCELLED: 0,
      NOSHOW: 0
    };

    data?.forEach(order => {
      if (counts.hasOwnProperty(order.status)) {
        // 모든 상태에서 payment_status가 'PAID'인 것만 카운트
        // (결제되지 않은 주문은 카운트에서 제외)
        if (order.payment_status === 'PAID') {
          counts[order.status as OrderStatus]++;
        }
      }
    });

    return {
      success: true,
      data: counts
    };
  } catch (error) {
    console.error('Order counts fetch error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '주문 카운트를 불러오는데 실패했습니다'
    };
  }
}

/**
 * 영업 상태를 토글합니다
 */
export async function toggleBusinessStatus(restaurantId: string, isOpen: boolean): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const { error } = await supabase
      .from('restaurants')
      .update({ 
        is_open: isOpen,
        updated_at: new Date().toISOString()
      })
      .eq('id', restaurantId);

    if (error) {
      console.error('Business status toggle error:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Business status toggle error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '영업 상태 변경에 실패했습니다'
    };
  }
}